import React from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Stack,
  Button,
} from "@mui/material";

import { ResumeStatisticData } from "../../../../types/resumes-managment/resume";
import MainCard from "../../../../components/MainCard";

interface ResumeStatisticProps {
  data: ResumeStatisticData | null | undefined;
}

const ResumeStatistic: React.FC<ResumeStatisticProps> = ({ data }) => {
  if (!data) {
    return null;
  }

  const user = data?.user;
  const english_resume = data?.english_resume;
  const arabic_resume = data?.arabic_resume;
  const applications = data?.applications;

  if (!user || !applications) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INACTIVE":
        return "error";
      default:
        return "default";
    }
  };

  const renderResumeCard = (resume: any, title: string) => {
    if (!resume) {
      return (
        <MainCard
          title={title}
          sx={{ height: "100%", opacity: 0.6, boxShadow: "none" }}
        >
          <Typography variant="body2" color="text.secondary">
            No resume available
          </Typography>
        </MainCard>
      );
    }

    return (
      <MainCard
        title={title}
        sx={{ height: "100%", boxShadow: "none" }}
        headerActions={
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              window.location.href = `/resumes/edit-resume/${resume.id}`;
            }}
          >
            Edit resume
          </Button>
        }
      >
        <Stack spacing={2}>
          <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
            <Typography color="GrayText" sx={{ width: "30%" }}>
              Status:
            </Typography>
            <Box sx={{ width: "70%" }}>
              <Chip
                label={resume.status ? "Active" : "Inactive"}
                color={resume.status ? "success" : "error"}
                size="small"
              />
            </Box>
          </Box>

          {resume.last_update && (
            <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
              <Typography color="GrayText" sx={{ width: "30%" }}>
                Last Update:
              </Typography>
              <Typography sx={{ width: "70%" }}>
                {resume.last_update}
              </Typography>
            </Box>
          )}

          {resume.reference_count !== undefined && (
            <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
              <Typography color="GrayText" sx={{ width: "30%" }}>
                References:
              </Typography>
              <Typography sx={{ width: "70%" }}>
                {resume.reference_count}
              </Typography>
            </Box>
          )}

          {resume.total_years_of_experience !== undefined && (
            <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
              <Typography color="GrayText" sx={{ width: "30%" }}>
                Experience:
              </Typography>
              <Typography sx={{ width: "70%" }}>
                {resume.total_years_of_experience} years
              </Typography>
            </Box>
          )}

          {resume.highest_education && (
            <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
              <Typography color="GrayText" sx={{ width: "30%" }}>
                Education:
              </Typography>
              <Typography sx={{ width: "70%" }}>
                {resume.highest_education}
              </Typography>
            </Box>
          )}

          {resume.field_of_work &&
            Object.keys(resume.field_of_work).length > 0 && (
              <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
                <Typography color="GrayText" sx={{ width: "30%" }}>
                  Field of Work:
                </Typography>
                <Box sx={{ width: "70%" }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {Object.entries(resume.field_of_work).map(
                      ([category, count]) => (
                        <Chip
                          key={category}
                          label={`${category}: ${count}`}
                          size="small"
                          variant="outlined"
                        />
                      )
                    )}
                  </Stack>
                </Box>
              </Box>
            )}
        </Stack>
      </MainCard>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <MainCard title="Applications Overview" sx={{ boxShadow: "none" }}>
          <Grid container spacing={2}>
            <Grid item xs={2.4}>
              <Paper sx={{ p: 2, textAlign: "center", boxShadow: "none" }}>
                <Typography variant="h4" color="primary">
                  {applications.total_applications || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Applications
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper sx={{ p: 2, textAlign: "center", boxShadow: "none" }}>
                <Typography variant="h4" color="primary">
                  {applications.read_applications || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Read Applications
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper sx={{ p: 2, textAlign: "center", boxShadow: "none" }}>
                <Typography variant="h4" color="primary">
                  {applications.total_notes || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Notes
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper sx={{ p: 2, textAlign: "center", boxShadow: "none" }}>
                <Typography variant="h4" color="primary">
                  {applications.total_emails || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Emails
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={2.4}>
              <Paper sx={{ p: 2, textAlign: "center", boxShadow: "none" }}>
                <Typography variant="h4" color="primary">
                  {applications.total_company_reports || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Company Reports
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {applications.last_application_date && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Last Application: {applications.last_application_date}
              </Typography>
            </Box>
          )}
        </MainCard>
      </Grid>
      <Grid item xs={12}>
        <MainCard title="User Information" sx={{ boxShadow: "none" }}>
          <Stack spacing={2}>
            {user.account_status && (
              <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
                <Typography color="GrayText" sx={{ width: "30%" }}>
                  Account Status:
                </Typography>
                <Box sx={{ width: "70%" }}>
                  <Chip
                    label={user.account_status}
                    color={getStatusColor(user.account_status) as any}
                    size="small"
                  />
                </Box>
              </Box>
            )}

            {user.user_name && (
              <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
                <Typography color="GrayText" sx={{ width: "30%" }}>
                  User Name:
                </Typography>
                <Typography sx={{ width: "70%" }}>{user.user_name}</Typography>
              </Box>
            )}

            {user.email && (
              <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
                <Typography color="GrayText" sx={{ width: "30%" }}>
                  Email:
                </Typography>
                <Typography sx={{ width: "70%" }}>{user.email}</Typography>
              </Box>
            )}

            {user.registration_date && (
              <Box sx={{ display: "flex", gap: "1rem", width: "100%" }}>
                <Typography color="GrayText" sx={{ width: "30%" }}>
                  Registration Date:
                </Typography>
                <Typography sx={{ width: "70%" }}>
                  {user.registration_date}
                </Typography>
              </Box>
            )}
          </Stack>
        </MainCard>
      </Grid>

      <Grid item xs={12} md={6}>
        {renderResumeCard(english_resume, "English Resume")}
      </Grid>

      <Grid item xs={12} md={6}>
        {renderResumeCard(arabic_resume, "Arabic Resume")}
      </Grid>
    </Grid>
  );
};

export default ResumeStatistic;
